// Interfaces
import { TInputData } from "@/Layouts/types/InputComponents/InputFormExtension";
import {
	IElrHroBonusContent,
	IInputDefinitions,
	TElrHroBonusModel,
	THeaderOptions,
	TOptionIdentifier,
	TTierBonus,
	TVariant,
} from "./interfaces";
import { ITierRow } from "@/Common/helpers/Matrices/interfaces";

// Constants
const options: THeaderOptions = [
	{ label: "Gross", value: "gross" },
	{ label: "ELR", value: "elr" },
	{ label: "Hrs/RO", value: "hrs" },
];

const ASC_AMOUNTS = "Ascending Amounts";

const TOOLTIPS_DEFINITIONS = {
	ELR: {
		headerTooltip: ASC_AMOUNTS,
		tiersTooltip: ASC_AMOUNTS,
	},
	HRO: {
		headerTooltip: ASC_AMOUNTS,
		tiersTooltip: ASC_AMOUNTS,
	},
};

const TIERS_LABELS = {
	ELR: "ELR ($)",
	HRO: "Hrs/RO",
};

const INPUT_DEFINITIONS: IInputDefinitions = {
	SELF: {
		gross: [
			{
				type: "Dollar",
				min: -10000,
				max: 1000000,
				decimals: 2,
				delta: 0.01,
				decimalsEditable: false,
			},
		],
		elr: [
			{
				type: "Dollar",
				min: -10000,
				max: 1000000,
				decimals: 2,
				delta: 0.01,
				decimalsEditable: false,
			},
		],
		hrs: [
			{
				type: "Unit",
				min: 0,
				max: 1000,
				decimals: 2,
				delta: 0,
				decimalsEditable: true,
			},
		],
	},
	STORE: {
		gross: [
			{
				type: "Dollar",
				min: -10000,
				max: 1000000,
				decimals: 2,
				delta: 0.01,
				decimalsEditable: false,
			},
			{
				type: "Percent",
				min: 0,
				max: 100,
				decimals: 2,
				delta: 0.01,
				decimalsEditable: true,
			},
		],
		elr: [
			{
				type: "Dollar",
				min: -10000,
				max: 1000000,
				decimals: 2,
				delta: 0.01,
				decimalsEditable: false,
			},
			{
				type: "Percent",
				min: 0,
				max: 100,
				decimals: 2,
				delta: 0.01,
				decimalsEditable: true,
			},
		],
		hrs: [
			{
				type: "Unit",
				min: -1000,
				max: 1000,
				decimals: 2,
				delta: 0,
				decimalsEditable: true,
			},
			{
				type: "Percent",
				min: 0,
				max: 100,
				decimals: 2,
				delta: 0.01,
				decimalsEditable: true,
			},
		],
	},
};

const getRelativeLabel = (relativeToStore: boolean) => (relativeToStore ? "STORE" : "SELF");

const getOptionIdentifierByVariantType = (variant: TVariant): TOptionIdentifier =>
	variant === "HRO" ? "hrs" : (variant.toLowerCase() as TOptionIdentifier);

const getInputsOptions = (option: TOptionIdentifier, relativeToStore = false) => {
	const relativeTo = getRelativeLabel(relativeToStore);
	return INPUT_DEFINITIONS[relativeTo][option];
};

const getInputOptionByType = (optionsParam: TInputData[], type: string) => {
	return optionsParam.find((option) => option.type === type) ?? optionsParam[0];
};

const getTiersInputType = (tiers: ITierRow[], inputAt = "from") => tiers?.[0]?.[inputAt]?.input ?? "Unknown";

const getHeaderOptions = (variant: TVariant) => {
	const variantType = getOptionIdentifierByVariantType(variant);
	return options.filter((option) => option.value !== variantType);
};

const getTabs = () => [
	{ key: "customer", tabName: "Customer Pay" },
	{ key: "internal", tabName: "Internal Pay" },
	{ key: "warranty", tabName: "Warranty Pay" },
];

const getTiersBonusInput = (tiers: TTierBonus[] = []) => tiers?.[0]?.["bonuses"]?.[0]?.["bonus"]?.["input"] ?? "Unknown";
const isDisabledGlobal = (tiers: TTierBonus[] = []) => {
	if (!tiers.length) {
		return false;
	}
	const input = getTiersBonusInput(tiers);
	return input === "Dollar";
};
const getGlobalDisabled = (model: TElrHroBonusModel | null, tabIdentifier: string) => {
	if (!model) {
		return false;
	}

	const { content } = model || {};
	const contentData = content?.[tabIdentifier as keyof Omit<IElrHroBonusContent, "_id">] ?? {};
	if (!contentData) {
		return false;
	}

	return isDisabledGlobal(contentData?.tiers ?? []);
};

const getSelectedOption = (value: TOptionIdentifier) => options.find((option) => option.value === value) ?? options[0];
const getTiersLabel = (variant: TVariant) => TIERS_LABELS[variant];
const getTooltips = (variant: TVariant) => TOOLTIPS_DEFINITIONS[variant];

const getInputStepValue = (input: string) => {
	if (input === "Unit") {
		return 0;
	}
	return 0.01;
};

function getInitialTiers(variant: "ELR" | "HRO", option: TOptionIdentifier): TTierBonus[] {
	const inputHeaderDefinitions = getInputsOptions(option, false);
	const optionIdentifier = getOptionIdentifierByVariantType(variant);
	const inputTiersDefinitions = getInputsOptions(optionIdentifier, false);

	const inputHeader = inputHeaderDefinitions[0];
	const inputTiers = inputTiersDefinitions[0];

	const { min: minRow = -1000, max: maxRow = 1000000, type: inputRow = "Dollar" } = inputTiers ?? {};
	const { min: minColumn = -1000, max: maxColumn = 1000000, type: inputColumn = "Dollar" } = inputHeader ?? {};

	return [
		{
			from: { value: minRow, input: inputRow },
			to: { value: maxRow, input: inputRow },
			bonuses: [
				{
					from: { value: minColumn, input: inputColumn },
					to: { value: maxColumn, input: inputColumn },
					bonus: { value: 0, input: "Dollar" },
				},
			],
		},
	];
}

const resetTiersOnInputsMismatch = (tiers: TTierBonus[], variant: TVariant, option: TOptionIdentifier) => {
	const inputHeaderDefinitions = getInputsOptions(option, false);
	const optionIdentifier = getOptionIdentifierByVariantType(variant);
	const inputTiersDefinitions = getInputsOptions(optionIdentifier, false);

	const inputHeaderType = inputHeaderDefinitions[0].type;
	const inputTiersType = inputTiersDefinitions[0].type;

	const mismatch = tiers[0]["from"]["input"] !== inputTiersType || tiers[0]["bonuses"][0]["from"]["input"] !== inputHeaderType;
	if (mismatch) {
		const updatedTiers = getInitialTiers(variant, option);
		return {
			tiers: updatedTiers,
			mismatch,
		};
	}

	return {
		tiers,
		mismatch,
	};
};

export {
	getGlobalDisabled,
	getHeaderOptions,
	getInputsOptions,
	getInputOptionByType,
	getInputStepValue,
	getOptionIdentifierByVariantType,
	getSelectedOption,
	getTabs,
	getTiersInputType,
	getTiersLabel,
	getTooltips,
	resetTiersOnInputsMismatch,
};
